import React, { useState, useEffect, useCallback } from 'react';
import { X, Mail, Trash2, Send, Pause, Play, Clock } from 'lucide-react';
import { reportSubscriptionsApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import StuiSelect from '../../components/common/StuiSelect';
import { formatTimestampDate } from '../../utils/dateTime';

/**
 * Subscribe / Manage popups for report subscriptions (DevPlan step 103 P4).
 *
 * YOU SUBSCRIBE YOURSELF. There is no recipient picker and no way to subscribe a colleague
 * (Danny, 2026-09-15). That is policy, not a missing feature: a subscribe-others capability
 * mails a report rendered under the OWNER's permissions to a third party who may have none —
 * a data-access bypass on a schedule. Self-only means the single recipient is the person whose
 * access is already checked, on create and again before every send.
 *
 * You see your own subscriptions; a SysAdmin sees everyone's, so there is someone who can clean
 * up after a departure. The scoping is enforced server-side, not here.
 *
 * ALSO DELIBERATELY ABSENT, so nobody adds it back by reflex:
 *   - No time-of-day and no timezone. The send time belongs to the JOB — one pass at 11:00Z,
 *     which is 7am Eastern. Decision D5 on step 103.
 *   - No Monthly. Daily or Weekly is the whole choice.
 *   - No separate-files toggle. That would mean multiple attachments, and the handler throws on
 *     it rather than silently mailing one combined file instead of five.
 *   - No PDF. Report PDFs render in the browser via @react-pdf/renderer, so a background job has
 *     nothing to call.
 */
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const ReportSubscriptionModal = ({
  mode,              // 'subscribe' | 'manage'
  reportSlug,
  reportName,
  dbSlug,
  recipe,            // required for 'subscribe' — the frozen export recipe
  currentUserId,
  onClose,
}) => {
  const { notify } = useNotifications();

  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const [format, setFormat] = useState('excel');
  const [frequency, setFrequency] = useState('Daily');
  const [dayOfWeek, setDayOfWeek] = useState(1);   // Monday, when Weekly

  // notify is deliberately NOT a dependency. It is only used on the error path, and depending on
  // it means this reloads whenever the notification state changes — which was an infinite
  // render/fetch loop back when the context handed out a fresh `notify` object every render (the
  // popup visibly flickered). The context memoises it now, but the dependency would still be
  // wrong: what this fetch depends on is which report and database to fetch for.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Subscribe is CONTEXTUAL — "you already have one for this report" — so it filters.
      // Manage is not: "My Subscriptions" means all of them, across every report and database.
      // You reach it from a report's menu, but that is where the button lives, not what it scopes to.
      const { data } = await reportSubscriptionsApi.list(
        mode === 'subscribe' ? { reportSlug, dbSlug } : {}
      );
      setSubs(data || []);
    } catch (err) {
      notify.error(err.response?.data?.error || 'Could not load existing subscriptions.');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, reportSlug, dbSlug]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!recipe) {
      notify.error('Run the report first — a subscription stores the filters you ran it with.');
      return;
    }
    setSaving(true);
    try {
      await reportSubscriptionsApi.create({
        reportSlug,
        reportName,
        databaseSlug: dbSlug,
        recipe,
        format,
        frequency,
        dayOfWeek: frequency === 'Weekly' ? dayOfWeek : null,
      });
      notify.success(
        `Subscribed — ${frequency === 'Weekly' ? DAY_NAMES[dayOfWeek] + 's' : 'daily'}, ${format.toUpperCase()}. Manage it from My Subscriptions.`, 5000);
      // Done: closing is what stops a second Subscribe click from creating a duplicate.
      onClose();
    } catch (err) {
      notify.error(err.response?.data?.error || 'Could not create the subscription.');
    } finally {
      setSaving(false);
    }
  };

  const act = async (id, fn, okMsg) => {
    setBusyId(id);
    try {
      await fn();
      if (okMsg) notify.success(okMsg, 4000);
      await load();
    } catch (err) {
      notify.error(err.response?.data?.error || 'Action failed.');
    } finally {
      setBusyId(null);
    }
  };

  const handleSendNow = async (id) => {
    setBusyId(id);
    // Immediate, sticky feedback. A cold send takes about 10 seconds — the report has to be
    // queried, rendered to XLSX and handed to Graph — and a disabled button is not enough signal
    // over that long: the first real use of this produced two identical emails because it looked
    // like nothing had happened. Timeout 0 keeps it up until the outcome replaces it.
    notify.status('Rendering and sending the report…', 0);
    try {
      const { data } = await reportSubscriptionsApi.sendNow(id);
      if (data.status === 'sent')
        notify.success(
          `Sent — ${data.rows?.toLocaleString() ?? '?'} rows, ${Math.round((data.bytes || 0) / 1024)} KB.`, 6000);
      else
        // A skip or failure carries its reason, and the reason is the useful part.
        notify.warning(`${data.status}: ${data.reason || 'no reason given'}`, 8000);
      await load();
    } catch (err) {
      notify.error(err.response?.data?.error || 'Send failed.');
    } finally {
      setBusyId(null);
    }
  };

  const cadence = (s) => s.frequency === 'Weekly' ? `${DAY_NAMES[s.dayOfWeek ?? 0]}s` : 'Daily';

  const isManage = mode === 'manage';

  // Only a SysAdmin ever sees rows they do not own, so the Owner column earns its place only then.
  const showOwner = subs.some(s => s.ownerId !== currentUserId);

  return (
    // Canonical popup skeleton per notes/ui-standards.md: stui-modal-overlay -> stui-modal ->
    // header/body/footer, with the BLUE stui-modal-header. The first cut used the legacy
    // sprint-add-task-* classes, which gave a white header (non-compliant) and left the two modes
    // looking unrelated to each other.
    <div className="stui-modal-overlay" onMouseDown={e => { if (e.target === e.currentTarget) onClose?.(); }}>
      {/* .stui-modal is a fixed 400px. Manage carries a six-column grid (report, database,
          cadence, format, owner, last run) and overran it, so it takes the documented
          stui-modal-lg variant at 720px. Subscribe stays 400: it is three fields, and a wide
          box round three selects looks broken in the other direction. The chrome, controls and
          skeleton are shared either way — that is what makes them feel like one thing. */}
      <div className={`stui-modal${isManage ? ' stui-modal-lg' : ''}`}>
        <div className="stui-modal-header">
          <h3><Mail size={18} />{isManage ? 'My Subscriptions' : 'Subscribe to this Report'}</h3>
          <button className="stui-modal-close" onClick={onClose} title="Close"><X size={20} /></button>
        </div>

        <div className="stui-modal-body">
          {mode === 'subscribe' && (
            <>
              <div className="stui-label-note">
                Emails <strong>{reportName}</strong> to you, with the filters you just ran, at 7am Eastern.
              </div>

              <div className="stui-field-group stui-label-left">
                <label>Format</label>
                <StuiSelect className="stui-select" value={format} onChange={e => setFormat(e.target.value)}>
                  <option value="excel">Excel (.xlsx)</option>
                  <option value="csv">CSV</option>
                  <option value="pdf">PDF</option>
                </StuiSelect>
              </div>

              {/* PDFs are rendered on the server and arrive as a download link, never an
                  attachment; the server checks the page count before accepting the subscription. */}
              {format === 'pdf' && (
                <div className="stui-label-note">
                  {recipe?.exportGroupSeparate
                    ? 'A PDF subscription delivers one file. Turn off "Separate files" on the report first, or choose Excel.'
                    : 'The email brings a link to download the PDF. Reports up to 750 pages; checking that can take a few seconds.'}
                </div>
              )}

              <div className="stui-field-group stui-label-left">
                <label>How often</label>
                <StuiSelect className="stui-select" value={frequency} onChange={e => setFrequency(e.target.value)}>
                  <option value="Daily">Daily</option>
                  <option value="Weekly">Weekly</option>
                </StuiSelect>
              </div>

              {frequency === 'Weekly' && (
                <div className="stui-field-group stui-label-left">
                  <label>Day</label>
                  <StuiSelect
                    className="stui-select"
                    value={dayOfWeek}
                    onChange={e => setDayOfWeek(Number(e.target.value))}
                  >
                    {DAY_NAMES.map((d, i) => <option key={d} value={i}>{d}</option>)}
                  </StuiSelect>
                </div>
              )}
            </>
          )}

          {loading ? (
            <div className="stui-hint">Loading…</div>
          ) : subs.length === 0 ? (
            mode === 'manage' && (
              <div className="stui-detail-empty">
                <Mail size={32} />
                <p>You have no report subscriptions.</p>
                <p className="stui-hint">Run a report, then use Subscribe on the same menu.</p>
              </div>
            )
          ) : (
            <>
              <div className="stui-detail-section-header">
                {mode === 'subscribe'
                  ? 'Already subscribed to this report'
                  : showOwner ? 'All subscriptions' : 'Your subscriptions'}
              </div>
              {/* stui-data-grid, not stui-ssg-reftable: these are real rows. The reftable class
                  is for the style guide's own reference tables — notes/ui-standards.md is explicit
                  that the two are not interchangeable. */}
              <div className="stui-data-grid-container">
              <div className="stui-data-grid-scroll-area">
              <table className="stui-data-grid">
                <thead>
                  <tr>
                    {/* Manage spans every report and database, so both have to be named — the
                        report name alone is ambiguous when the same report is subscribed on two
                        projects, which is a normal thing to do. */}
                    {isManage && <th>Report</th>}
                    {isManage && <th>Database</th>}
                    <th>Cadence</th>
                    <th>Format</th>
                    {showOwner && <th>Owner</th>}
                    <th>Last run</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {subs.map(s => (
                    <tr key={s.id} style={{ opacity: s.isEnabled ? 1 : 0.55 }}>
                      {isManage && <td>{s.reportName}</td>}
                      {isManage && <td>{s.databaseSlug}</td>}
                      <td>{cadence(s)}{!s.isEnabled && ' (paused)'}</td>
                      <td>{s.format}</td>
                      {showOwner && <td>{s.ownerName}</td>}
                      <td title={s.lastError || ''}>
                        {s.lastRunUtc
                          ? `${formatTimestampDate(s.lastRunUtc)} · ${s.lastStatus}`
                          : 'never'}
                        {s.consecutiveFailures > 0 && ` · ${s.consecutiveFailures} failure(s)`}
                      </td>
                      <td>
                        <button
                          className="stui-entry-action-btn"
                          title={busyId === s.id ? 'Sending…' : 'Send now'}
                          // Every row's actions lock while one is sending, not just this row's:
                          // sends are serialised behind JobExecutionGate server-side, so a second
                          // click anywhere would just collect a 409.
                          disabled={busyId !== null || !s.isEnabled}
                          onClick={() => handleSendNow(s.id)}
                        >{busyId === s.id ? <Clock size={11} /> : <Send size={11} />}</button>
                        <button
                          className="stui-entry-action-btn"
                          title={s.isEnabled ? 'Pause' : 'Resume'}
                          disabled={busyId !== null}
                          onClick={() => act(
                            s.id,
                            () => reportSubscriptionsApi.update(s.id, { isEnabled: !s.isEnabled }),
                            s.isEnabled ? 'Paused.' : 'Resumed.')}
                        >{s.isEnabled ? <Pause size={11} /> : <Play size={11} />}</button>
                        <button
                          className="stui-entry-action-btn"
                          title="Delete"
                          disabled={busyId !== null}
                          onClick={() => act(s.id, () => reportSubscriptionsApi.remove(s.id), 'Deleted.')}
                        ><Trash2 size={11} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              </div>
            </>
          )}
        </div>

        <div className="stui-modal-footer">
          <button type="button" className="stui-btn stui-btn-secondary" onClick={onClose}>
            {isManage ? 'Close' : 'Cancel'}
          </button>
          {!isManage && (
            <button type="button" className="stui-btn stui-btn-primary" onClick={handleCreate} disabled={saving}>
              {saving ? 'Subscribing…' : 'Subscribe'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportSubscriptionModal;
